import { useState, useEffect, useCallback } from 'react';
import { adminAPI, seedAPI, bookingsAPI } from '../lib/api';
import { useNotificationEvents } from '../contexts/NotificationContext';
import { Clock, BarChart3, Users, Shield, Database, DollarSign, CreditCard, RefreshCw, CheckCircle, XCircle, Play, AlertCircle, Wifi } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { AdminPageShell, StatCard, EmptyRow } from '../components/AdminShared';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  available: { label: 'Available', color: 'bg-blue-500/10 text-blue-600 border-blue-500/20', icon: Users },
  pending: { label: 'Pending', color: 'bg-amber-500/10 text-amber-600 border-amber-500/20', icon: Clock },
  confirmed: { label: 'Confirmed', color: 'bg-green-500/10 text-green-600 border-green-500/20', icon: CheckCircle },
  in_progress: { label: 'In Progress', color: 'bg-purple-500/10 text-purple-600 border-purple-500/20', icon: Play },
  completed: { label: 'Completed', color: 'bg-muted-foreground/10 text-muted-foreground', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-destructive/10 text-destructive', icon: XCircle },
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [interpreterStats, setInterpreterStats] = useState([]);
  const [filter, setFilter] = useState('active');

  const loadStats = useCallback(async () => {
    try { const res = await adminAPI.getStats(); setStats(res.data); } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  const loadBookings = useCallback(async () => {
    try {
      const bRes = await bookingsAPI.list();
      setBookings(bRes.data || []);
      try {
        const sRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/interpreter-stats`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (sRes.ok) setInterpreterStats(await sRes.json());
      } catch (e) { /* stats optional */ }
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadStats(); loadBookings(); }, [loadStats, loadBookings]);

  // Real-time updates via WS
  const { connected: wsConnected } = useNotificationEvents(useCallback((event) => {
    if (['new_assignment', 'assignment_taken', 'assignment_cancelled'].includes(event.type)) {
      loadBookings();
    }
  }, [loadBookings]));

  // Fallback polling
  useEffect(() => {
    if (wsConnected) return;
    const interval = setInterval(loadBookings, 30000);
    return () => clearInterval(interval);
  }, [wsConnected, loadBookings]);

  const handleSeed = async () => {
    setSeeding(true);
    try { await seedAPI.seed(); await loadStats(); await loadBookings(); } catch (e) { console.error(e); }
    setSeeding(false);
  };

  const handleSendConfirmations = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bookings/send-confirmations`, {
        method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      toast.success(`${data.confirmations_sent} confirmation requests sent`);
      loadBookings();
    } catch (e) { toast.error('Failed to send confirmations'); }
  };

  // ── Metrics ──
  const metrics = [
    { label: 'Total Sessions', value: stats?.total_sessions || 0, icon: Clock },
    { label: 'Active Sessions', value: stats?.active_sessions || 0, icon: BarChart3 },
    { label: 'Total Users', value: stats?.total_users || 0, icon: Users },
    { label: 'Online Interpreters', value: stats?.total_interpreters || 0, icon: Shield },
    { label: 'Revenue', value: `$${((stats?.total_revenue_cents || 0) / 100).toFixed(2)}`, icon: DollarSign, color: 'text-[hsl(var(--success))]' },
    { label: 'Credits Issued', value: `$${((stats?.total_credits_cents || 0) / 100).toFixed(2)}`, icon: CreditCard, color: 'text-[hsl(var(--warning))]' },
  ];

  // ── Assignment stats ──
  const assignmentStats = {
    available: bookings.filter(b => b.status === 'available').length,
    pending: bookings.filter(b => b.status === 'pending').length,
    confirmed: bookings.filter(b => b.status === 'confirmed').length,
    in_progress: bookings.filter(b => b.status === 'in_progress').length,
    completed: bookings.filter(b => b.status === 'completed').length,
    cancelled: bookings.filter(b => b.status === 'cancelled').length,
    needsAttention: bookings.filter(b => b.status === 'pending' || (b.status === 'confirmed' && b.confirmation_sent && !b.reconfirmed)).length,
  };

  const filtered = bookings.filter(b => {
    if (filter === 'active') return ['available', 'pending', 'confirmed', 'in_progress'].includes(b.status);
    if (filter === 'all') return true;
    return b.status === filter;
  }).sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));

  const getTimeUntil = (scheduledAt) => {
    const diff = new Date(scheduledAt) - new Date();
    if (diff < 0) return 'Past due';
    const hours = Math.floor(diff / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    if (hours > 24) return `${Math.floor(hours / 24)}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const isUrgent = (scheduledAt) => {
    const diff = new Date(scheduledAt) - new Date();
    return diff > 0 && diff < 2 * 3600000;
  };

  return (
    <AdminPageShell
      loading={loading}
      title="System Overview"
      subtitle="Platform metrics, revenue, and live assignments"
      testId="admin-dashboard"
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleSeed} disabled={seeding} data-testid="admin-seed-button">
            <Database className="w-4 h-4 mr-2" />{seeding ? 'Seeding...' : 'Seed Demo Data'}
          </Button>
          {wsConnected && (
            <span className="flex items-center gap-1 text-[10px] text-green-500 px-2"><Wifi className="w-3 h-3" />Live</span>
          )}
        </div>
      }
    >
      {/* Row 1: Platform Metrics + Revenue + Credits */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {metrics.map((s, i) => <StatCard key={i} {...s} index={i} />)}
      </div>

      {/* Row 2: Assignment Board */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-base" style={{ fontFamily: 'DM Sans' }}>Assignments</h2>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleSendConfirmations} data-testid="send-confirmations-btn">
              <AlertCircle className="w-4 h-4 mr-1" />Send 24h Confirmations
            </Button>
            <Button variant="outline" size="sm" onClick={() => { loadBookings(); }} data-testid="refresh-board-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Assignment Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {[
            { key: 'available', label: 'Available', count: assignmentStats.available, color: 'text-blue-500' },
            { key: 'pending', label: 'Pending', count: assignmentStats.pending, color: 'text-amber-500' },
            { key: 'confirmed', label: 'Confirmed', count: assignmentStats.confirmed, color: 'text-green-500' },
            { key: 'in_progress', label: 'In Progress', count: assignmentStats.in_progress, color: 'text-purple-500' },
            { key: 'completed', label: 'Completed', count: assignmentStats.completed, color: 'text-muted-foreground' },
            { key: 'cancelled', label: 'Cancelled', count: assignmentStats.cancelled, color: 'text-destructive' },
            { key: 'needsAttention', label: 'Needs Attention', count: assignmentStats.needsAttention, color: 'text-red-500' },
          ].map(s => (
            <button key={s.key} onClick={() => setFilter(s.key === 'needsAttention' ? 'active' : s.key)}
              className={`p-3 rounded-lg border bg-card text-left transition-all hover:border-[hsl(var(--brand))]/40 ${filter === s.key ? 'border-[hsl(var(--brand))] ring-1 ring-[hsl(var(--brand))]/20' : ''}`}
              data-testid={`stat-${s.key}`}>
              <p className="text-xs text-muted-foreground">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`}>{s.count}</p>
            </button>
          ))}
        </div>

        {/* Filter */}
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48 h-9" data-testid="board-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active Assignments</SelectItem>
              <SelectItem value="all">All Assignments</SelectItem>
              <SelectItem value="available">Available Only</SelectItem>
              <SelectItem value="pending">Pending Only</SelectItem>
              <SelectItem value="confirmed">Confirmed Only</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">{filtered.length} assignment{filtered.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Assignment Table */}
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[80px]">Status</TableHead>
                <TableHead>Language</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Interpreter</TableHead>
                <TableHead>Scheduled</TableHead>
                <TableHead className="w-[80px]">Time Until</TableHead>
                <TableHead className="w-[60px]">Duration</TableHead>
                <TableHead>Coverage</TableHead>
                <TableHead>Flags</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(b => {
                const sc = STATUS_CONFIG[b.status] || STATUS_CONFIG.pending;
                const Icon = sc.icon;
                const urgent = isUrgent(b.scheduled_at);
                const offeredCount = b.offered_to?.length || 0;
                const declinedCount = b.declined_by?.length || 0;
                const iStats = interpreterStats.find(s => s.interpreter_id === b.interpreter_id);
                return (
                  <TableRow key={b.id} className={urgent ? 'bg-amber-500/5' : ''} data-testid={`board-row-${b.id}`}>
                    <TableCell>
                      <Badge className={`text-xs ${sc.color}`}>
                        <Icon className="w-3 h-3 mr-1" />{sc.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium text-sm">{b.target_language?.toUpperCase()}</span>
                      <span className="text-xs text-muted-foreground ml-1">({b.domain_level})</span>
                    </TableCell>
                    <TableCell className="text-sm">{b.user_name || '—'}</TableCell>
                    <TableCell className="text-sm">
                      {b.interpreter_name || (b.status === 'available' ? <span className="text-muted-foreground italic">Awaiting acceptance</span> : <span className="text-muted-foreground">—</span>)}
                      {iStats && iStats.handback_rate > 20 && (
                        <Badge variant="outline" className="ml-1 text-[10px] text-amber-600 border-amber-500/30">{iStats.handback_rate}% handback</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs font-mono">{new Date(b.scheduled_at).toLocaleString()}</TableCell>
                    <TableCell>
                      <span className={`text-xs font-medium ${urgent ? 'text-red-500 font-bold' : 'text-muted-foreground'}`}>
                        {getTimeUntil(b.scheduled_at)}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{b.duration_minutes}m</TableCell>
                    <TableCell>
                      {b.status === 'available' && (
                        <div className="flex items-center gap-1">
                          <Badge variant="outline" className="text-[10px]">{offeredCount} offered</Badge>
                          {declinedCount > 0 && <Badge variant="outline" className="text-[10px] text-destructive border-destructive/30">{declinedCount} declined</Badge>}
                        </div>
                      )}
                      {b.status === 'confirmed' && <Badge className="text-[10px] bg-green-500/10 text-green-600 border-green-500/20">Assigned</Badge>}
                      {b.status === 'in_progress' && <Badge className="text-[10px] bg-purple-500/10 text-purple-600 border-purple-500/20">Active</Badge>}
                      {b.actual_minutes && <span className="text-xs text-muted-foreground ml-1">{b.actual_minutes}m actual</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {b.handback_count > 0 && <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-500/30">{b.handback_count}x handback</Badge>}
                        {b.confirmation_sent && !b.reconfirmed && <Badge variant="outline" className="text-[10px] text-red-500 border-red-500/30">Unconfirmed</Badge>}
                        {b.reconfirmed && <Badge variant="outline" className="text-[10px] text-green-600 border-green-500/30">Reconfirmed</Badge>}
                        {b.cancellation_fee > 0 && <Badge variant="outline" className="text-[10px] text-destructive">{b.cancellation_fee} credits fee</Badge>}
                        {urgent && <Badge className="text-[10px] bg-red-500/10 text-red-500 border-red-500/30">Urgent</Badge>}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && <EmptyRow colSpan={9} message="No assignments matching filter" />}
            </TableBody>
          </Table>
        </div>

        {/* Interpreter Hand-Back Stats */}
        {interpreterStats.length > 0 && (
          <div className="rounded-lg border bg-card">
            <div className="p-4 border-b">
              <h3 className="font-semibold text-sm" style={{ fontFamily: 'DM Sans' }}>Interpreter Hand-Back Statistics</h3>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Interpreter ID</TableHead>
                  <TableHead>Total Accepted</TableHead>
                  <TableHead>Hand-Backs</TableHead>
                  <TableHead>Hand-Back Rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {interpreterStats.map(s => (
                  <TableRow key={s.interpreter_id} data-testid={`stats-row-${s.interpreter_id}`}>
                    <TableCell className="font-mono text-xs">{s.interpreter_id}</TableCell>
                    <TableCell>{s.total_accepted || 0}</TableCell>
                    <TableCell>{s.handback_count || 0}</TableCell>
                    <TableCell>
                      <Badge variant={s.handback_rate > 20 ? 'destructive' : 'outline'} className="text-xs">
                        {s.handback_rate || 0}%
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </AdminPageShell>
  );
}
